import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Form,
  Alert,
  Spinner,
  Modal,
  Table,
} from "react-bootstrap";
// import { IconPackage } from "@tabler/icons-react";
import Select from "react-select";
import { API_URL } from "utils/constants";
import { getRequest } from "services/api";
import { usePurchaseOrder } from "../context/PurchaseOrderContext";
import PrintJson from "utils/printJson";

const PurchaseReceiptForm = ({
  initialData,
  onSuccess,
  onError,
  show,
  onHide,
}) => {
  // Use purchase order context
  const { purchaseOrders, filterOptions, handleStatusFilter } =
    usePurchaseOrder();

  // Filter purchase orders to only show "ordered" and "partially_received" status for selection
  const orderedPurchaseOrders = purchaseOrders.data.filter(
    (po) => po.status === "ordered" || po.status === "partially_received"
  );

  // Create options for the select
  const purchaseOrderOptions = orderedPurchaseOrders.map((po) => ({
    value: po.id,
    label: `${po.po_number} - ${po.supplier?.name} (${po.warehouse?.name})`,
  }));

  // Helper function to format numbers with thousand separators
  // const formatRupiah = (number) => {
  //   return new Intl.NumberFormat("id-ID").format(number);
  // };

  // Format number for display (like MasterDataFormV2)
  const formatNumber = (value) => {
    const number = value.replace(/\D/g, "");
    return new Intl.NumberFormat("id-ID").format(Number(number));
  };

  // Unformat number for storage
  const unformatNumber = (value) => {
    return value.replace(/\./g, "");
  };

  // Form state
  const [formData, setFormData] = useState({
    purchase_order_id: "",
    note: "",
    items: [],
  });

  // Selected options for display
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [purchaseOrderData, setPurchaseOrderData] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const isUpdateMode = initialData && initialData.id;

  // Initialize form data for edit mode
  useEffect(() => {
    if (isUpdateMode && initialData) {
      setFormData({
        purchase_order_id: initialData.purchase_order_id || "",
        note: initialData.note || "",
        items: initialData.items || [],
      });

      if (initialData.purchase_order) {
        setSelectedPurchaseOrder({
          value: initialData.purchase_order.id,
          label: `${initialData.purchase_order.po_number} - ${initialData.purchase_order.supplier?.name}`,
        });
        setPurchaseOrderData(initialData.purchase_order);
      }
    }
  }, [initialData, isUpdateMode]);

  // Set status filter to "ordered" for create mode to load ordered purchase orders
  useEffect(() => {
    if (!isUpdateMode && filterOptions.status !== "ordered") {
      handleStatusFilter("ordered");
    }
  }, [isUpdateMode, filterOptions.status, handleStatusFilter]);

  // Load purchase order details when selected
  useEffect(() => {
    if (formData.purchase_order_id && !isUpdateMode) {
      loadPurchaseOrderDetails(formData.purchase_order_id);
    }
  }, [formData.purchase_order_id, isUpdateMode]);

  const loadPurchaseOrderDetails = async (poId) => {
    try {
      const response = await getRequest(`/purchase-orders/${poId}`);
      if (response.success) {
        setPurchaseOrderData(response.data);

        // Initialize receipt items based on PO items
        const receiptItems = response.data.items.map((poItem) => ({
          purchase_order_item_id: poItem.id,
          inventory_id: poItem.inventory_id,
          quantity_received: poItem.quantity_ordered || 0, // Default to ordered quantity, user can modify
          actual_unit_price: poItem.unit_price || 0,
          note: "",
        }));

        setFormData((prev) => ({
          ...prev,
          items: receiptItems,
        }));
      }
    } catch (error) {
      console.error("Error loading purchase order details:", error);
      setSubmitError("Failed to load purchase order details");
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...formData.items];

    let processedValue = value;

    // Handle number formatting for quantity_received and actual_unit_price like MasterDataFormV2
    if (field === "quantity_received" || field === "actual_unit_price") {
      if (typeof value === "string") {
        // If it's a string input, unformat it for storage
        processedValue = field === "quantity_received"
          ? parseInt(unformatNumber(value)) || 0
          : parseFloat(unformatNumber(value)) || 0;
      } else {
        processedValue = value;
      }
    }

    updatedItems[index] = { ...updatedItems[index], [field]: processedValue };
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.purchase_order_id) {
      newErrors.purchase_order_id = "Purchase Order is required";
    }

    if (!formData.note || formData.note.trim() === "") {
      newErrors.note = "Note is required";
    }

    // Check if at least one item has quantity_received > 0
    const hasValidItems = formData.items.some(
      (item) => item.quantity_received > 0
    );
    if (!hasValidItems) {
      newErrors.items = "At least one item must have quantity received > 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      // Filter out items with zero quantity received
      const filteredItems = formData.items.filter(
        (item) => item.quantity_received > 0
      );

      const payload = {
        ...formData,
        items: filteredItems,
      };

      const url = isUpdateMode
        ? `${API_URL}/purchase-receipts/${initialData.id}`
        : `${API_URL}/purchase-receipts`;

      const response = await fetch(url, {
        method: isUpdateMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess(result);
      }

      if (onHide) {
        onHide();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage = error.message || "Failed to save purchase receipt";
      setSubmitError(errorMessage);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    // Validate form for reject action (note is required)
    const newErrors = {};

    if (!formData.purchase_order_id) {
      newErrors.purchase_order_id = "Purchase Order is required";
    }

    if (!formData.note || formData.note.trim() === "") {
      newErrors.note = "Note is required";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      // Get current user for cancelled_by field
      const userData = localStorage.getItem("user");
      const user = userData ? JSON.parse(userData) : null;

      // Filter out items with zero quantity received (optional for cancel)
      const filteredItems = formData.items.filter(
        (item) => item.quantity_received > 0
      );

      const payload = {
        ...formData,
        items: filteredItems,
        cancelled_by: user?.id || "",
        cancellation_reason: formData.note || "Cancelled by user", // Use note as cancellation reason
      };

      const url = `${API_URL}/purchase-receipts/cancel`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess(result);
      }

      if (onHide) {
        onHide();
      }
    } catch (error) {
      console.error("Error cancelling purchase receipt:", error);
      const errorMessage = error.message || "Failed to cancel purchase receipt";
      setSubmitError(errorMessage);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    setFormData({
      purchase_order_id: "",
      note: "",
      items: [],
    });
    setSelectedPurchaseOrder(null);
    setPurchaseOrderData(null);
    setErrors({});
    setSubmitError("");

    if (onHide) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>Proses Purchase Receipt</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {submitError && (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          )}

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Purchase Order *</Form.Label>
                <Select classNamePrefix="react-select" className="react-select-container"
                  options={purchaseOrderOptions}
                  value={selectedPurchaseOrder}
                  onChange={(selected) => {
                    setSelectedPurchaseOrder(selected);
                    handleFormChange(
                      "purchase_order_id",
                      selected ? selected.value : ""
                    );
                  }}
                  isSearchable
                  placeholder="Select purchase order..."
                  isClearable
                  isDisabled={isUpdateMode}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: errors.purchase_order_id
                        ? "#dc3545"
                        : "#dee2e6",
                      "&:hover": {
                        borderColor: errors.purchase_order_id
                          ? "#dc3545"
                          : "#adb5bd",
                      },
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
                {errors.purchase_order_id && (
                  <div className="text-danger small mt-1">
                    {errors.purchase_order_id}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {purchaseOrderData && (
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">Purchase Order Details</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>PO Number:</strong> {purchaseOrderData.po_number}
                    </p>
                    <p>
                      <strong>Supplier:</strong>{" "}
                      {purchaseOrderData.supplier?.name}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Warehouse:</strong>{" "}
                      {purchaseOrderData.warehouse?.name}
                    </p>
                    <p>
                      <strong>Requester:</strong>{" "}
                      {purchaseOrderData.requester?.name}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Note *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.note}
                  onChange={(e) => handleFormChange("note", e.target.value)}
                  placeholder="Enter receipt note"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Items Section */}
          {formData.items.length > 0 && (
            <Card className="mt-3">
              <Card.Header>
                <h6 className="mb-0">Receipt Items</h6>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Item Name</th>
                        <th className="text-center">Ordered Qty</th>
                        <th>Received Qty</th>
                        <th>Unit Price(Rp)</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => {
                        // Find the corresponding PO item for ordered quantity
                        const poItem = purchaseOrderData?.items?.find(
                          (poItem) => poItem.id === item.purchase_order_item_id
                        );

                        return (
                          <tr key={index}>
                            <td>
                              <strong>
                                {poItem?.inventory?.name ||
                                  item.inventory?.name ||
                                  "Unknown Item"}
                              </strong>
                            </td>
                            <td className="text-center"
                            >{poItem?.quantity_ordered || 0}</td>
                            <td>
                              <Form.Control
                                type="text"
                                value={formatNumber(String(item.quantity_received || 0))}
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    "quantity_received",
                                    e.target.value
                                  )
                                }
                                style={{ width: "80px" }}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  type="text"
                                  value={formatNumber(String(Math.floor(item.actual_unit_price || 0)))}
                                  onChange={(e) =>
                                    updateItem(
                                      index,
                                      "actual_unit_price",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.note}
                                onChange={(e) =>
                                  updateItem(index, "note", e.target.value)
                                }
                                placeholder="Item note"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {errors.items && (
            <Alert variant="warning" className="mt-3">
              {errors.items}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {formData.purchase_order_id && formData.note && formData.note.trim() !== "" ? (
            <div className="d-flex justify-content-between w-100">
              <Button
                variant="danger"
                size="lg"
                onClick={handleReject}
                disabled={loading}
              >
                {loading && <Spinner size="sm" className="me-2" />}
                Reject
              </Button>
              <Button variant="primary" size="lg" type="submit" disabled={loading}>
                {loading && <Spinner size="sm" className="me-2" />}
                {isUpdateMode ? "Update" : "Approve"} Receipt
              </Button>
            </div>
          ) : (
            <div className="text-center w-100">
              <small className="text-muted">
                Please select a Purchase Order and fill in the note field to enable action buttons
              </small>
            </div>
          )}
        </Modal.Footer>
      </Form>
      <PrintJson data={formData} title="Purchase Receipt Form Data" />
    </Modal>
  );
};

export default PurchaseReceiptForm;
